import { CheckIcon, ChevronsUpDownIcon, Loader2Icon } from "lucide-react";
import { useMemo, useState } from "react";
import { Command, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { triggerClasses } from "@/components/ui/entity-combobox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { type FeedbackProject, useFeedbackProjects } from "@/hooks/use-adhoc-feedback";
import { cn } from "@/lib/utils";

/**
 * Rendering a thousand rows into the open list makes typing lag, and nobody
 * scrolls that far — they type. The rest are one keystroke away.
 */
const VISIBLE_LIMIT = 100;

/**
 * The "which project is this about" list box on Give feedback — every active
 * NOS project, alphabetical, filtered as you type. Not an `EntityCombobox`:
 * those read Enable's own tables through `/api/options`, and projects live
 * only in NOS.
 *
 * The list is fetched the first time it opens, so the dialog doesn't pay for
 * it when nobody tags a project.
 */
export function FeedbackProjectPicker({
  id,
  value,
  onChange,
}: {
  id?: string;
  value: FeedbackProject | null;
  onChange: (project: FeedbackProject | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Once opened, keep the list: closing and reopening shouldn't refetch.
  const [wanted, setWanted] = useState(false);
  const projects = useFeedbackProjects(wanted);

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    const all = projects.data ?? [];
    return term ? all.filter((p) => p.projectName.toLowerCase().includes(term)) : all;
  }, [projects.data, query]);
  const shown = matches.slice(0, VISIBLE_LIMIT);

  function pick(project: FeedbackProject | null) {
    onChange(project);
    setOpen(false);
    setQuery("");
  }

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setWanted(true);
        else setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          className={triggerClasses()}
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}>
            {value?.projectName ?? "No specific project"}
          </span>
          <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0" style={{ width: "var(--radix-popover-trigger-width)" }}>
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={setQuery} placeholder="Filter projects…" />
          <CommandList>
            <CommandItem value="__none__" onSelect={() => pick(null)}>
              <CheckIcon className={cn("size-4", value ? "opacity-0" : "opacity-100")} />
              <span className="text-muted-foreground">No specific project</span>
            </CommandItem>
            {projects.isError ? (
              <div className="px-2 py-6 text-center text-destructive text-sm">
                {projects.error.message}.{" "}
                <button type="button" className="underline" onClick={() => void projects.refetch()}>
                  Retry
                </button>
              </div>
            ) : projects.isLoading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground text-sm">
                <Loader2Icon className="size-4 animate-spin" />
                Loading projects from NOS…
              </div>
            ) : shown.length === 0 ? (
              <div className="py-6 text-center text-muted-foreground text-sm">
                No projects match.
              </div>
            ) : (
              <>
                {shown.map((p) => (
                  <CommandItem key={p.projectId} value={p.projectId} onSelect={() => pick(p)}>
                    <CheckIcon
                      className={cn(
                        "size-4",
                        value?.projectId === p.projectId ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {p.projectName}
                  </CommandItem>
                ))}
                {matches.length > shown.length && (
                  <p className="px-2 py-2 text-center text-muted-foreground text-xs">
                    Showing {shown.length} of {matches.length}. Type to narrow the list.
                  </p>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
